import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { analyticsService } from '../../../application/AnalyticsService';
import { DateChartAnalytics } from '../../../domain/analytics/DateChartAnalytics';
import { BinTypes } from '../../../interfaces/analytics';
import { formatDateByBinType } from '../../../pages/analytics/shared/ChartHeaderFilters';
import useErrorMiddleware from '../../errorMiddleware/useErrorMiddleware';
import { useRetrieveActiveProject } from '../../project/useRetrieveActiveProject';

import { useRetrieveUser } from '../../user/useRetrieveUser';
import { useRetrieveUserCredentials } from '../../user/useRetrieveUserCredentials';

interface CancellationDataProps {
  date: string;
  dateLabel: string;
  nobody: number;
  wrong_address: number;
  missing_information: number;
  other: number;
  total: number;
  previousChange?: number;
}

type UseRetrieveAnalyticsCancellationReasonsResponse = ServiceResponse<null, CancellationDataProps[]> & {
  data: CancellationDataProps[];
};

// Hooks act as 'Adapter' layer.
export const useRetrieveAnalyticsCancellationReasons = ({
  startDate,
  endDate,
  binType,
  vehicleId,
}: {
  startDate: Date;
  endDate: Date;
  binType: BinTypes; // TODO: REMOVE FROM INTERFACES
  vehicleId?: string;
}): UseRetrieveAnalyticsCancellationReasonsResponse => {
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: user } = useRetrieveUser();
  const { data: userCredentials } = useRetrieveUserCredentials();

  const [isLoading, setIsLoading] = useState(true);
  const [nobodyResponse, setNobodyResponse] = useState<DateChartAnalytics[] | undefined>();
  const [wrongAddressResponse, setWrongAddressResponse] = useState<DateChartAnalytics[] | undefined>();
  const [missingInformationResponse, setMissingInformationResponse] = useState<DateChartAnalytics[] | undefined>();
  const [otherResponse, setOtherResponse] = useState<DateChartAnalytics[] | undefined>();
  const [cancellationReasonsData, setCancellationReasonsData] = useState<CancellationDataProps[] | undefined>();

  useQuery(
    [`planner_analytics_cancellation_reasons`, startDate, endDate, binType, vehicleId, `nobody`],
    () =>
      analyticsService.getAnalyticsCancellationReasons({
        startDate,
        endDate,
        binType,
        vehicleId,
        sequence: `nobody`,
        projectId: activeProject!.id,
      }),
    {
      enabled: !!user && !!activeProject && userCredentials?.subscription.test(`planner.analytics`),
      retry: false,
      refetchOnWindowFocus: false,
      onSuccess: (response: DateChartAnalytics[]) => {
        setNobodyResponse(response);
      },
      onError: (err: any) => {
        console.info(`Planner analytics cancellation reasons error`, err?.response);

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }
      },
    }
  );
  useQuery(
    [`planner_analytics_cancellation_reasons`, startDate, endDate, binType, vehicleId, `wrong_address`],
    () =>
      analyticsService.getAnalyticsCancellationReasons({
        startDate,
        endDate,
        binType,
        vehicleId: vehicleId,
        sequence: `wrong_address`,
        projectId: activeProject!.id,
      }),
    {
      enabled: !!user && !!activeProject && userCredentials?.subscription.test(`planner.analytics`),
      retry: false,
      refetchOnWindowFocus: false,
      onSuccess: (response: DateChartAnalytics[]) => {
        setWrongAddressResponse(response);
      },
      onError: (err: any) => {
        console.info(`Planner analytics cancellation reasons error`, err?.response);

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }
      },
    }
  );
  useQuery(
    [`planner_analytics_cancellation_reasons`, startDate, endDate, binType, vehicleId, `missing_information`],
    () =>
      analyticsService.getAnalyticsCancellationReasons({
        startDate,
        endDate,
        binType,
        vehicleId,
        sequence: `missing_information`,
        projectId: activeProject!.id,
      }),
    {
      enabled: !!user && !!activeProject && userCredentials?.subscription.test(`planner.analytics`),
      retry: false,
      refetchOnWindowFocus: false,
      onSuccess: (response: DateChartAnalytics[]) => {
        setMissingInformationResponse(response);
      },
      onError: (err: any) => {
        console.info(`Planner analytics cancellation reasons error`, err?.response);

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }
      },
    }
  );
  useQuery(
    [`planner_analytics_cancellation_reasons`, startDate, endDate, binType, vehicleId, `other`],
    () =>
      analyticsService.getAnalyticsCancellationReasons({
        startDate,
        endDate,
        binType,
        vehicleId: vehicleId,
        sequence: `other`,
        projectId: activeProject!.id,
      }),
    {
      enabled: !!user && !!activeProject && userCredentials?.subscription.test(`planner.analytics`),
      retry: false,
      refetchOnWindowFocus: false,
      onSuccess: (response: DateChartAnalytics[]) => {
        setOtherResponse(response);
      },
      onError: (err: any) => {
        console.info(`Planner analytics cancellation reasons error`, err?.response);

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }
      },
    }
  );

  useEffect(() => {
    if (!!nobodyResponse && !!wrongAddressResponse && !!missingInformationResponse && !!otherResponse) {
      setIsLoading(false);

      const locale = user!.language.value;

      const newData: CancellationDataProps[] = [];

      nobodyResponse.forEach((p, index) => {
        const otherValue = (otherResponse.find((i) => i.date === p.date) ?? {}).value;
        const wrongAddressValue = (wrongAddressResponse.find((f) => f.date === p.date) ?? {}).value;
        const missingInformationValue = (missingInformationResponse.find((f) => f.date === p.date) ?? {}).value;
        const dateLabel = formatDateByBinType(p.date, binType, locale);

        const total = (missingInformationValue ?? 0) + (wrongAddressValue ?? 0) + (otherValue ?? 0) + p.value;

        let previousChange;

        // Calculates the previous period change
        if (index > 0) {
          const previousTotal = newData[index - 1].total;

          const difference = total - previousTotal;

          previousChange = 0;

          if (difference !== 0) {
            previousChange = previousTotal > 0 ? Math.floor((difference / previousTotal) * 100) : 100;
          }
        }

        newData.push({
          date: p.date,
          dateLabel,
          nobody: p.value ?? 0,
          wrong_address: wrongAddressValue ?? 0,
          missing_information: missingInformationValue ?? 0,
          other: otherValue ?? 0,
          total,
          previousChange,
        } as CancellationDataProps);
      });

      setCancellationReasonsData(newData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nobodyResponse, wrongAddressResponse, missingInformationResponse, otherResponse]);

  return {
    error: false,
    isLoading: isLoading,
    success: false,
    run: null,
    data: cancellationReasonsData || [],
  };
};

export default useRetrieveAnalyticsCancellationReasons;
